import React, { useState } from 'react';
import { Users } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { useEntitlements } from '../../hooks/useEntitlements';
import { PlanBadge } from '../Common';
import { formatRoleName } from '../../utils/formatters';

interface TeamSwitcherProps {
  className?: string;
  /** Hide plan/role badges (e.g. when rendered inside UserMenu which shows them separately) */
  hideBadges?: boolean;
}

/**
 * Dropdown component for switching between teams the user belongs to.
 * Shows for staff users (for testing) or users with multiple teams.
 */
export function TeamSwitcher({ className = '', hideBadges = false }: TeamSwitcherProps) {
  const { teamMemberships, activeTeam, setActiveTeam, loading: userLoading, isStaff, isSuperadmin } = useUser();
  const { refresh: refreshEntitlements } = useEntitlements();
  const [switching, setSwitching] = useState(false);

  // Don't render if still loading or no teams
  if (userLoading || teamMemberships.length === 0) {
    return null;
  }

  // Show dropdown only for staff/superadmin (testing) or users with multiple teams
  const showDropdown = isStaff || isSuperadmin || teamMemberships.length > 1;

  const handleTeamChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTeamId = e.target.value;
    if (newTeamId === activeTeam?.team_id) return;

    setSwitching(true);
    try {
      const success = await setActiveTeam(newTeamId);
      if (success) {
        // Refresh permissions after team switch
        await refreshEntitlements();
      }
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className={`flex items-center gap-2 min-w-0 ${className}`}>
      <div className="flex items-center gap-2 bg-paper-2 rounded px-2 py-1 min-w-0 flex-1 max-w-full">
        <Users size={16} strokeWidth={1.25} className="text-ink-500 flex-shrink-0" />
        {showDropdown ? (
          <select
            value={activeTeam?.team_id || ''}
            onChange={handleTeamChange}
            disabled={switching}
            className="text-sm bg-transparent text-ink-900 focus:outline-none disabled:opacity-50 cursor-pointer pr-6 w-full min-w-0 truncate"
            title="Switch team context"
          >
            {teamMemberships.map(tm => (
              <option key={tm.team_id} value={tm.team_id}>
                {tm.team_name || 'Unknown Team'}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-sm text-ink-900 truncate">
            {activeTeam?.team_name || teamMemberships[0]?.team_name || 'Unknown Team'}
          </span>
        )}
      </div>
      {!hideBadges && (activeTeam || teamMemberships[0]) && (
        <div className="hidden lg:flex items-center gap-1.5 text-xs">
          <PlanBadge planId={activeTeam?.plan_id || teamMemberships[0]?.plan_id} size="xs" />
          <span className="text-ink-500">
            {formatRoleName(activeTeam?.role_id || teamMemberships[0]?.role_id)}
          </span>
        </div>
      )}
      {switching && (
        <span className="text-xs text-ink-500 animate-pulse">Switching...</span>
      )}
    </div>
  );
}

export default TeamSwitcher;
